import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import * as classes from './css';
import { optionShape } from './optionShape';
import { Tooltip } from '@cvent/carina-popover';
import { injectTestId } from '@cvent/nucleus-test-automation';

export default class Option extends React.Component {
  static propTypes = {
    /** ID applied to menuitem element */
    id: PropTypes.string,
    option: PropTypes.shape(optionShape).isRequired,
    /** callback to be executed when `Option` is selected, returns `option` object */
    onUpdate: PropTypes.func,
    /** carina theme object */
    theme: PropTypes.object.isRequired,
    /** focused controls whether to focus the item in the DOM */
    focused: PropTypes.bool,
    /** adds 'selected' styling to option */
    selected: PropTypes.bool,
    /** index of Option in Options array */
    optionIndex: PropTypes.number,
    /** function that sets Magazine state to optionIndex parameter */
    setCurrentFocus: PropTypes.func,
    /** hasError triggers the error state styles */
    hasError: PropTypes.bool,
    /** Text direction */
    isRtl: PropTypes.bool,
    /** required prop will set accessibility-related input attributes */
    required: PropTypes.bool,
    /** pass down testID from parent */
    testID: PropTypes.string,
    optionStyleObject: PropTypes.object
  };

  static defaultProps = {
    hasError: false,
    focused: false,
    required: false,
    selected: false
  };

  state = {
    hasChanged: false
  };

  optionRef;
  setOptionRef = element => (this.optionRef = element);

  componentDidMount() {
    this.focusOption();
  }

  componentDidUpdate() {
    this.focusOption();
  }

  componentWillUnmount() {
    this.optionRef && // eslint-disable-line no-unused-expressions
      this.optionRef.removeEventListener('animationend', this.handleSelect);
  }

  focusOption = () =>
    setTimeout(
      () => this.optionRef && this.props.focused && this.optionRef.focus(),
      100
    );

  handleClick = event => {
    event.stopPropagation();
    if (!(this.props.option.heading || this.props.option.disabled)) {
      if (this.optionRef) this.optionRef.focus();
      this.handleSelect();
      this.setState({ hasChanged: true });
    }
  };

  handleSelect = () => {
    /* wait for select animation to end */
    if (this.props.onUpdate)
      setTimeout(
        () => this.props.onUpdate(this.props.option, this.props.optionIndex),
        parseFloat(this.props.theme.transition.duration.default) * 1000
      );
  };

  handleMouseEnter = () => {
    if (
      this.optionRef &&
      // this needs to be here to stop some really strange scrolling behavior. If this check isn't here, there's like
      // one pixel at the bottom where, if hovered over, the list will scroll quickly and jumpily.
      this.optionRef.parentElement.scrollTop >
        this.optionRef.offsetTop - this.optionRef.parentElement.clientHeight + 1
    ) {
      this.optionRef.focus();
      this.props.setCurrentFocus(this.props.optionIndex);
    }
  };
  handleKeyDown = event => {
    if (
      !(this.props.option.heading || this.props.option.disabled) &&
      /* ^ Screen reader will allow us to select these ^ */
      this.optionRef &&
      event.key === 'Enter'
    ) {
      this.optionRef.focus();
      this.handleSelect(event);
      this.setState({ hasChanged: true });
    }
  };

  render() {
    const {
      optionIndex,
      hasError,
      isRtl,
      theme,
      option,
      selected,
      focused,
      optionStyleObject,
      id
    } = this.props;
    const { heading, disabled, lineBreak, destructive } = option;
    const listItemCss = [
      classes.listItemCss({
        theme,
        hasError,
        heading,
        isRtl,
        isSelected: selected,
        optionStyleObject
      }),

      heading ? classes.optionHeadingCss(theme) : {},
      heading && this.props.optionIndex === 0 ? classes.firstOptionHeading : {},
      disabled ? classes.optionDisabledCss(theme) : {},
      destructive ? classes.optionDestructiveCss(theme) : {}
    ];

    const listItem = (
      <li
        id={id}
        css={[
          listItemCss,
          classes.listItemBackground(
            theme,
            selected,
            focused,
            heading,
            disabled,
            destructive
          ),
          this.state.hasChanged ? classes.listItemAnimationCss(theme) : {}
        ]}
        tabIndex={-1}
        key={optionIndex}
        onClick={this.handleClick}
        onMouseDown={event => event.preventDefault()}
        onMouseEnter={this.handleMouseEnter}
        onKeyDown={this.handleKeyDown}
        role={heading ? 'rowgroup' : 'menuitem'}
        ref={this.setOptionRef}
        aria-selected={this.heading ? undefined : this.selected}
        {...injectTestId(
          this.props.testID &&
            `${this.props.testID}-option${
              this.props.optionIndex !== undefined
                ? `-${this.props.optionIndex}`
                : ''
            }`
        )}
        {...this.checkForAriaDisabled()}
        {...this.checkForDestructiveAria()}
      >
        <div css={classes.labelContainer}>{this.formatLabel()}</div>
        {this.formatSecondary()}
      </li>
    );
    return (
      <Fragment>
        {lineBreak && <div css={classes.lineBreakCss(theme)} />}
        {(this.props.option.tooltipText && (
          <Tooltip
            portal
            text={this.props.option.tooltipText}
            trigger={listItem}
          />
        )) ||
          listItem}
      </Fragment>
    );
  }

  formatLabel = () => {
    const label = this.props.option.label;
    if (typeof label === 'string') {
      return label;
    }
    return label(this.props.theme);
  };

  formatSecondary = () => {
    if (this.props.option.heading) return null;
    return (
      <ul css={classes.secondaryContainerCss(this.props.theme)}>
        {this.props.option.secondary &&
          this.props.option.secondary.map((secondary, index) => (
            <li css={classes.secondaryListItemCss()} key={index}>
              {typeof secondary === 'string' ? secondary : secondary.value}
            </li>
          ))}
      </ul>
    );
  };

  checkForAriaDisabled = () =>
    this.props.option.disabled ? { 'aria-disabled': true } : {};

  checkForDestructiveAria = () =>
    this.props.option.destructive
      ? {
          'aria-label': `${
            typeof this.props.option.label === 'string'
              ? this.props.option.label
              : this.props.option.value
          }, option is destructive`
        }
      : {};
}
